import { filter, groupBy, keyBy, map, orderBy } from "lodash-es";

import {
  AssistantTaskStatus,
  type AssistantFollowUpObservable,
  type AssistantProjectObservable,
  type AssistantTaskObservable,
} from "@/store/assistant";
import { getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import { buildAgentSearchableText } from "@/pages/agent/utils/agent-search.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import { TaskSortKey } from "@/pages/agent/tasks/use-agent-tasks-search-params";

export enum TaskSectionKind {
  Open = "open",
  Completed = "completed",
}

export type TaskRowViewModel = {
  task: AssistantTaskObservable;
  project: AssistantProjectObservable | undefined;
  projectTitle: string;
  nextFollowUp: AssistantFollowUpObservable | undefined;
  lastFollowUp: AssistantFollowUpObservable | undefined;
  isClosed: boolean;
  searchableText: string;
};

export const DEFAULT_TASK_STATUS_FILTER_VALUES = [
  AssistantTaskStatus.Pending,
  AssistantTaskStatus.InProgress,
  AssistantTaskStatus.Completed,
  AssistantTaskStatus.Canceled,
];

const CLOSED_TASK_STATUS_VALUES = new Set<string>([
  AssistantTaskStatus.Completed,
  AssistantTaskStatus.Canceled,
]);

export const isClosedTaskStatus = ({ status }: { status: string }) =>
  CLOSED_TASK_STATUS_VALUES.has(normalizeStatusValue({ status }));

const getNextFollowUp = ({ followUps }: { followUps: AssistantFollowUpObservable[] }) => {
  const pendingFollowUps = filter(followUps, followUp => !isClosedFollowUp({ followUp }));

  return orderBy(
    pendingFollowUps,
    followUp =>
      getAgentTimestamp({
        value: followUp.scheduledFor ?? followUp.dueAt,
        fallback: Number.POSITIVE_INFINITY,
      }),
    "asc"
  )[0];
};

const getLastFollowUp = ({ followUps }: { followUps: AssistantFollowUpObservable[] }) =>
  orderBy(
    followUps,
    followUp =>
      getAgentTimestamp({
        value:
          followUp.completedAt ?? followUp.firedAt ?? followUp.scheduledFor ?? followUp.updatedAt,
        fallback: 0,
      }),
    "desc"
  )[0];

/**
 * Combines task, project, and follow-up sync models into rows for the tasks view.
 */
export const buildTaskRows = ({
  tasks,
  projects,
  followUps,
}: {
  tasks: AssistantTaskObservable[];
  projects: AssistantProjectObservable[];
  followUps: AssistantFollowUpObservable[];
}) => {
  const projectsById = keyBy(projects, project => project.id);
  const followUpsWithTask = filter(followUps, followUp => Boolean(followUp.taskId));
  const followUpsByTaskId = groupBy(followUpsWithTask, followUp => followUp.taskId ?? "");

  const visibleTasks = filter(tasks, task => !task.hasPendingDelete);

  return map(visibleTasks, task => {
    const project = task.projectId ? projectsById[task.projectId] : undefined;
    const projectTitle = project?.title ?? "No project";
    const taskFollowUps = followUpsByTaskId[task.id] ?? [];
    const title = task.title || "Untitled task";

    return {
      task,
      project,
      projectTitle,
      nextFollowUp: getNextFollowUp({ followUps: taskFollowUps }),
      lastFollowUp: getLastFollowUp({ followUps: taskFollowUps }),
      isClosed: isClosedTaskStatus({ status: task.status }),
      searchableText: buildAgentSearchableText({
        values: [title, task.description, projectTitle, task.status],
      }),
    } satisfies TaskRowViewModel;
  });
};

const sortByDueDate = ({ taskRows }: { taskRows: TaskRowViewModel[] }) =>
  orderBy(
    taskRows,
    [
      taskRow =>
        getAgentTimestamp({
          value: taskRow.task.dueAt,
          fallback: Number.POSITIVE_INFINITY,
        }),
      taskRow =>
        getAgentTimestamp({
          value: taskRow.task.updatedAt,
          fallback: 0,
        }),
    ],
    ["asc", "desc"]
  );

const sortByRecentlyUpdated = ({ taskRows }: { taskRows: TaskRowViewModel[] }) =>
  orderBy(
    taskRows,
    taskRow =>
      getAgentTimestamp({
        value: taskRow.task.updatedAt,
        fallback: 0,
      }),
    "desc"
  );

export const getSortedOpenTaskRows = ({
  taskRows,
  sortKey,
}: {
  taskRows: TaskRowViewModel[];
  sortKey: TaskSortKey;
}) => {
  if (sortKey === TaskSortKey.RecentlyUpdated) {
    return sortByRecentlyUpdated({ taskRows });
  }

  return sortByDueDate({ taskRows });
};

export const getSortedCompletedTaskRows = ({
  taskRows,
  sortKey,
}: {
  taskRows: TaskRowViewModel[];
  sortKey: TaskSortKey;
}) => {
  if (sortKey === TaskSortKey.DueDate) {
    return sortByDueDate({ taskRows });
  }

  return sortByRecentlyUpdated({ taskRows });
};
